import { useMemo, useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  FinanceBudgetDetailEmployeesDataCollectionDocument,
  ExpenseBudgetAllocationFragment,
  FinanceBudgetsDetailBudgetFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { useTeams } from 'modules/employees/hooks/useTeams'
import { amountWithCurrency } from 'modules/finance/lib/budgets'
import { allocationStatusVariant } from 'modules/finance/lib/budgetTagInfo'

import { AddEmployeesModal } from './AddEmployeesModal'
import { EmployeeDetail } from './EmployeeDetail'

interface Props {
  budget: FinanceBudgetsDetailBudgetFragment
  locale: string
}

interface Filters {
  teamId?: string[]
}

const mapFiltersToGraphqlFilters = (filters: Filters) => {
  return {
    ...(filters.teamId?.length && {
      accessTeam: { in: filters.teamId },
    }),
  }
}

export const EmployeesDataCollection = ({ budget, locale }: Props) => {
  const [selectedItem, setSelectedItem] = useState<ExpenseBudgetAllocationFragment | null>(null)
  const [isAddEmployeesModalOpen, setIsAddEmployeesModalOpen] = useState(false)
  const { teams } = useTeams()

  // Separate query to get employee IDs that already have allocations
  // Uses cache-first to leverage the data-collection's cache when available
  const { data: allocationsData } = useQuery(FinanceBudgetDetailEmployeesDataCollectionDocument, {
    variables: {
      budgetId: budget.id,
      sortOrder: { field: 'id', order: 'asc' as const },
    },
    fetchPolicy: 'cache-first',
  })

  const employeeIdsWithBudget = useMemo(() => {
    const allocations = allocationsData?.finance?.budgetAllocationsConnection?.edges || []
    return allocations
      .map((edge) => edge.node.employee?.id)
      .filter((id): id is string => id !== undefined)
  }, [allocationsData])

  const teamOptions = useMemo(
    () => teams.map((team) => ({ label: team.name, value: team.id })),
    [teams]
  )

  const source = useGraphqlDataCollectionSource({
    document: FinanceBudgetDetailEmployeesDataCollectionDocument,
    mapData: (data): ExpenseBudgetAllocationFragment[] => {
      return data.finance.budgetAllocationsConnection.edges.map((edge) => edge.node)
    },
    mapPagination: (data) => ({
      total: data.finance.budgetAllocationsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ filters }) => {
      return {
        budgetId: budget.id,
        sortOrder: {
          field: 'id',
          order: 'asc' as const,
        },
        filter: mapFiltersToGraphqlFilters(filters),
      }
    },
    filters: {
      teamId: {
        label: i18n.t('common.team'),
        type: 'in',
        options: {
          options: teamOptions,
        },
      },
    },
    secondaryActions: {
      expanded: 1,
      actions: () => [
        {
          label: i18n.t('budgets.detail.add_employee'),
          onClick: () => setIsAddEmployeesModalOpen(true),
          disabled: budget.archived,
        },
      ],
    },
    itemOnClick: (item: ExpenseBudgetAllocationFragment) => () => setSelectedItem(item),
  })

  const emptyStates = budget.archived
    ? {
        'no-data': {
          title: i18n.t('budgets.expense_budget_detail.employees.empty_state.title'),
          description: i18n.t('budgets.expense_budget_detail.employees.empty_state.description'),
          emoji: '👥',
        },
      }
    : {
        'no-data': {
          title: i18n.t('budgets.expense_budget_detail.employees.empty_state.title'),
          description: i18n.t('budgets.expense_budget_detail.employees.empty_state.description'),
          emoji: '👥',
          actions: [
            {
              label: i18n.t('budgets.detail.add_employee'),
              onClick: () => setIsAddEmployeesModalOpen(true),
            },
          ],
        },
      }

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('budgets.detail.tabs.employees.table.employee'),
                  render: (item: ExpenseBudgetAllocationFragment) => {
                    if (!item.employee) {
                      return { type: 'text' as const, value: '-' }
                    }
                    return {
                      type: 'person' as const,
                      value: {
                        firstName: item.employee.fullName.split(' ')[0] || '',
                        lastName: item.employee.fullName.split(' ').slice(1).join(' ') || '',
                        src: item.employee.avatar?.url || undefined,
                      },
                    }
                  },
                },
                {
                  label: i18n.t('budgets.detail.tabs.employees.table.allocation'),
                  render: (item: ExpenseBudgetAllocationFragment) => ({
                    type: 'text' as const,
                    value:
                      item.amountCents != null
                        ? amountWithCurrency(Number(item.amountCents), budget.currency, locale)
                        : '-',
                  }),
                },
                {
                  label: i18n.t('budgets.detail.tabs.employees.table.reported'),
                  render: (item: ExpenseBudgetAllocationFragment) => ({
                    type: 'text' as const,
                    value: amountWithCurrency(
                      Number(item.reportedAmountCents || 0),
                      budget.currency,
                      locale
                    ),
                  }),
                },
                {
                  label: i18n.t('budgets.detail.tabs.employees.table.available'),
                  render: (item: ExpenseBudgetAllocationFragment) => {
                    const status = item.status
                    if (!status || item.availableAmountCents == null) {
                      return { type: 'text' as const, value: '-' }
                    }
                    return {
                      type: 'status' as const,
                      value: {
                        label: amountWithCurrency(
                          Math.abs(Number(item.availableAmountCents)),
                          budget.currency,
                          locale
                        ),
                        status: allocationStatusVariant(status),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
        emptyStates={emptyStates}
      />
      {selectedItem && (
        <EmployeeDetail item={selectedItem} budget={budget} onClose={() => setSelectedItem(null)} />
      )}
      {isAddEmployeesModalOpen && (
        <AddEmployeesModal
          budget={budget}
          onClose={() => setIsAddEmployeesModalOpen(false)}
          employeeIdsWithBudget={employeeIdsWithBudget}
        />
      )}
    </>
  )
}
